import React, {
  useEffect,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  useSelector,
  useDispatch,
} from "react-redux";

import {
  motion,
  AnimatePresence,
} from "framer-motion";

import {
  getAllCategorys,
} from "../../redux/actions/category";

import {
  MealCategoryCard,
  Loader,
} from "../utils";

import {
  FiGrid,
  FiArrowDown,
} from "react-icons/fi";


function CategoriesSection() {
  const {
    t,
  } = useTranslation();

  const dispatch =
    useDispatch();


  /* =====================================
      REDUX - CATEGORIES
  ====================================== */

  const {
    isLoading,
    AllCategorys,
  } = useSelector(
    (state) =>
      state.Categorys
  );


  /* =====================================
      GLOBAL MOBILE VIEW

      one = عمود واحد
      two = عمودان

      الافتراضي = عمودان
  ====================================== */

  const mobileMealColumns =
    useSelector(
      (state) =>
        state.Layout
          ?.mobileMealColumns ||
        "two"
    );


  /* =====================================
      MOBILE DETECTION
  ====================================== */

  const [
    isMobile,
    setIsMobile,
  ] = useState(() => {
    if (
      typeof window ===
      "undefined"
    ) {
      return false;
    }

    return (
      window.innerWidth <
      768
    );
  });


  useEffect(() => {
    const handleResize =
      () => {
        setIsMobile(
          window.innerWidth <
            768
        );
      };


    window.addEventListener(
      "resize",
      handleResize
    );


    handleResize();


    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, []);


  /* =====================================
      LOAD CATEGORIES
  ====================================== */

  useEffect(() => {
    dispatch(
      getAllCategorys(
        1,
        100
      )
    );
  }, [
    dispatch,
  ]);


  /* =====================================
      SAFE DATA
  ====================================== */

  const categories =
    Array.isArray(
      AllCategorys
    )
      ? AllCategorys
      : [];


  /* =====================================
      CARD MODE

      compact=true فقط:
      - الهاتف
      - عمودان

      من md فما فوق يعود الكرت
      إلى التصميم الكامل تلقائياً.
  ====================================== */

  const compactCard =
    isMobile &&
    mobileMealColumns ===
      "two";


  return (
    <section
      className="
        relative

        overflow-hidden

        bg-back-secondary
        dark:bg-back-darkprimery

        py-16
        md:py-24
        lg:py-28
        xl:py-32
      "
    >

      {/* =====================================
          BACKGROUND DECORATIONS
      ====================================== */}

      <div
        className="
          absolute

          -top-40
          -end-40

          w-[420px]
          h-[420px]

          rounded-full

          border
          border-color-1/10

          pointer-events-none
        "
      />


      <div
        className="
          absolute

          top-10
          end-0

          w-[300px]
          h-[300px]

          rounded-full

          bg-color-1/10

          blur-[120px]

          pointer-events-none
        "
      />


      <div
        className="
          absolute

          -bottom-32
          -start-32

          w-[360px]
          h-[360px]

          rounded-full

          bg-color-2/5

          blur-[100px]

          pointer-events-none
        "
      />


      <div
        className="
          container

          relative
          z-10
        "
      >

        {/* =====================================
            HEADER
        ====================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 30,
          }}

          whileInView={{
            opacity: 1,
            y: 0,
          }}

          transition={{
            duration: 0.7,
          }}

          viewport={{
            once: true,
            amount: 0.2,
          }}

          className="
            grid

            grid-cols-1

            lg:grid-cols-[0.75fr_1.25fr]

            gap-6
            md:gap-8
            lg:gap-14

            items-end

            mb-9
            md:mb-14
            lg:mb-16
          "
        >

          {/* =================================
              SMALL TITLE
          ================================= */}

          <div>

            <div
              className="
                inline-flex

                items-center

                gap-2.5
                md:gap-3

                mb-4
              "
            >

              <span
                className="
                  w-10
                  md:w-12

                  h-[2px]

                  rounded-full

                  bg-color-1
                "
              />


              <FiGrid
                className="
                  text-color-1

                  text-sm
                  md:text-lg
                "
              />


              <span
                className="
                  font-kufi

                  text-[10px]
                  sm:text-xs
                  md:text-sm

                  text-color-1
                "
              >
                {t(
                  "Yaman menu categories"
                )}
              </span>

            </div>


            <p
              className="
                hidden
                lg:block

                max-w-sm

                font-kufi

                text-sm

                leading-7

                text-n-5
                dark:text-tcolor-3
              "
            >
              {t(
                "choose your favorite category"
              )}
            </p>

          </div>


          {/* =================================
              MAIN HEADING
          ================================= */}

          <div>

            <h2
              className="
                max-w-3xl

                font-kufi
                font-bold

                text-tcolor-4
                dark:text-tcolor-1

                text-[1.8rem]
                leading-[1.55]

                sm:text-3xl

                md:text-5xl
                md:leading-[1.45]

                lg:text-[3.5rem]
                lg:leading-[1.4]

                mb-4
                md:mb-5
              "
            >
              {t(
                "discover the flavors of Yaman"
              )}
            </h2>


            <p
              className="
                max-w-3xl

                font-kufi

                text-[11px]
                sm:text-sm
                md:text-base

                leading-7
                md:leading-8

                text-n-6
                dark:text-tcolor-2
              "
            >
              {t(
                "Yaman categories description"
              )}
            </p>

          </div>

        </motion.div>


        {/* =====================================
            DIVIDER
        ====================================== */}

        <div
          className="
            flex

            items-center

            gap-4

            mb-7
            md:mb-10
          "
        >

          <div
            className="
              flex-1

              h-px

              bg-color-2/10
              dark:bg-white/10
            "
          />


          <div
            className="
              flex

              w-9
              h-9

              md:w-10
              md:h-10

              shrink-0

              items-center
              justify-center

              rounded-full

              border
              border-color-1/20

              bg-color-1/5

              text-color-1
            "
          >
            <FiArrowDown />
          </div>


          <div
            className="
              flex-1

              h-px

              bg-color-2/10
              dark:bg-white/10
            "
          />

        </div>


        {/* =====================================
            CONTENT
        ====================================== */}

        {isLoading &&
        categories.length ===
          0 ? (

          <div
            className="
              min-h-[320px]

              flex
              items-center
              justify-center
            "
          >
            <Loader />
          </div>

        ) : (

          <>
            {categories.length >
            0 ? (

              <AnimatePresence
                mode="wait"
              >

                <motion.div
                  key={
                    mobileMealColumns
                  }

                  initial={{
                    opacity: 0,
                  }}

                  animate={{
                    opacity: 1,
                  }}

                  exit={{
                    opacity: 0,
                  }}

                  transition={{
                    duration: 0.3,
                  }}

                  className={`
                    grid

                    ${
                      mobileMealColumns ===
                      "two"
                        ? `
                          grid-cols-2
                          gap-3
                        `
                        : `
                          grid-cols-1
                          gap-6
                        `
                    }

                    md:grid-cols-2
                    md:gap-7

                    xl:grid-cols-3
                    xl:gap-8
                  `}
                >

                  {categories.map(
                    (
                      category,
                      index
                    ) => (

                      <motion.div
                        key={
                          category?._id ||
                          index
                        }

                        initial={{
                          opacity: 0,
                          y: 25,
                        }}

                        animate={{
                          opacity: 1,
                          y: 0,
                        }}

                        transition={{
                          duration: 0.45,

                          delay:
                            Math.min(
                              index *
                                0.04,
                              0.25
                            ),
                        }}

                        className="
                          min-w-0
                          h-full
                        "
                      >

                        <MealCategoryCard
                          category={
                            category
                          }

                          index={
                            index
                          }

                          compact={
                            compactCard
                          }
                        />

                      </motion.div>

                    )
                  )}

                </motion.div>

              </AnimatePresence>

            ) : (

              /* =================================
                  EMPTY STATE
              ================================= */

              <motion.div
                initial={{
                  opacity: 0,
                  y: 20,
                }}

                animate={{
                  opacity: 1,
                  y: 0,
                }}

                transition={{
                  duration: 0.5,
                }}

                className="
                  rounded-[2rem]

                  border
                  border-color-1/15

                  bg-back-primery
                  dark:bg-back-darksecondary

                  px-6
                  py-14

                  md:py-16

                  text-center
                "
              >

                <div
                  className="
                    flex

                    w-16
                    h-16

                    mx-auto
                    mb-5

                    items-center
                    justify-center

                    rounded-full

                    bg-color-1/10

                    text-color-1
                  "
                >
                  <FiGrid
                    size={24}
                  />
                </div>


                <h3
                  className="
                    font-kufi
                    font-semibold

                    text-lg
                    md:text-2xl

                    text-tcolor-4
                    dark:text-tcolor-1

                    mb-3
                  "
                >
                  {t(
                    "no categories available"
                  )}
                </h3>


                <p
                  className="
                    max-w-lg

                    mx-auto

                    font-kufi

                    text-xs
                    md:text-sm

                    leading-7

                    text-n-6
                    dark:text-tcolor-2
                  "
                >
                  {t(
                    "Yaman categories will appear here"
                  )}
                </p>

              </motion.div>

            )}
          </>

        )}

      </div>

    </section>
  );
}


export default CategoriesSection;